import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Loader from '../../components/Loader/Loader';
import Error from '../../components/Error/Error';
import { getNewsArticles, getNewsroom } from '../../shared/repos/graphql/news';
import endpoints from '../../shared/constants/endpoints';
import NewsArticleCard from './components/NewsArticleCard';
import NewsPagination from './components/NewsPagination';
import NewsSearch from './components/NewsSearch';
import MultimediaTiles from './components/MultimediaTiles';
import {
  NEWSROOM_DESCRIPTION, NEWSROOM_TITLE, buildListSearch, parseListParams,
} from './helpers';
import styles from './News.module.css';

const NewsIndex = () => {
  const location = useLocation();
  const history = useHistory();
  const { q, page } = parseListParams(location.search);
  const isLanding = !q && page === 1;

  const [newsroom, setNewsroom] = useState(null);
  const [list, setList] = useState({ loading: true, error: false, articles: [], paginatorInfo: null });

  useEffect(() => {
    let cancelled = false;
    getNewsroom()
      .then(({ data }) => {
        if (!cancelled) setNewsroom(data.newsroom);
      })
      .catch(() => {
        if (!cancelled) setNewsroom({ featured: [], multimediaThumbnails: {} });
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setList(prev => ({ ...prev, loading: true, error: false }));
    getNewsArticles({ search: q, page })
      .then(({ data }) => {
        if (cancelled) return;
        setList({
          loading: false,
          error: false,
          articles: data.newsArticles.data,
          paginatorInfo: data.newsArticles.paginatorInfo,
        });
      })
      .catch(() => {
        if (!cancelled) setList({ loading: false, error: true, articles: [], paginatorInfo: null });
      });
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [q, page]);

  const handleSearch = query => {
    history.push({ pathname: endpoints.news, search: buildListSearch({ q: query, page: 1 }) });
  };

  const outOfRange = !list.loading && list.paginatorInfo && page > Math.max(list.paginatorInfo.lastPage, 1);

  if (list.error || outOfRange) {
    return (
      <>
        <Helmet><title>{NEWSROOM_TITLE}</title></Helmet>
        <Error errorType={list.error ? '400' : '404'} />
      </>
    );
  }

  const featured = (isLanding && newsroom?.featured) || [];
  const [hero, ...secondary] = featured;
  const featuredSlugs = new Set(featured.map(article => article.slug));
  const articles = list.articles.filter(article => !featuredSlugs.has(article.slug));
  const listTitle = q ? `Search results for “${q}”` : 'Latest News';
  const showListTitle = list.loading || articles.length > 0 || !hero;
  const title = page > 1 ? `Newsroom - Page ${page} | Insomnia Cookies` : NEWSROOM_TITLE;

  return (
    <div className={styles.pageWrapper} data-region="secondary-page" data-page="NewsIndex">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={NEWSROOM_DESCRIPTION} />
        <meta property="og:title" content={NEWSROOM_TITLE} />
        <meta property="og:description" content={NEWSROOM_DESCRIPTION} />
      </Helmet>
      <div className={`${styles.pageContainer} text-dark dark:text-white`} data-part="content-surface">
        <div className={styles.header}>
          <h1 className={`${styles.pageTitle} mb-0`}>Newsroom</h1>
          <NewsSearch initialQuery={q} onSearch={handleSearch} />
        </div>

        {hero && (
          <section aria-label="Featured news" className={styles.featuredSection}>
            <NewsArticleCard article={hero} featured />
            {secondary.length > 0 && (
              <div className={`${styles.cardGrid} ${styles.featuredGrid}`}>
                {secondary.map(article => <NewsArticleCard key={article.slug} article={article} />)}
              </div>
            )}
          </section>
        )}

        <section aria-label={listTitle}>
          {showListTitle && <h2 className={styles.sectionTitle}>{listTitle}</h2>}
          {list.loading && <Loader id="loader" />}
          {!list.loading && articles.length === 0 && !hero && (
            <p className="font-montserratMedium text-lg">
              {q ? 'No news matched your search.' : 'No news yet. Check back soon.'}
            </p>
          )}
          {!list.loading && articles.length > 0 && (
            <div className={styles.cardGrid}>
              {articles.map(article => <NewsArticleCard key={article.slug} article={article} />)}
            </div>
          )}
          {!list.loading && list.paginatorInfo && (
            <NewsPagination
              currentPage={list.paginatorInfo.currentPage}
              lastPage={list.paginatorInfo.lastPage}
              query={q}
            />
          )}
        </section>

        {isLanding && newsroom && (
          <MultimediaTiles thumbnails={newsroom.multimediaThumbnails || {}} mediaKitUrl={newsroom.mediaKitUrl} />
        )}
      </div>
    </div>
  );
};

export default NewsIndex;
